import type { InitialStockEntry } from "@/lib/api/products";
import { effectiveCostPrice } from "@/lib/products";

/**
 * One opening-stock projection for Review, create submission, and the success
 * summary. Matrix quantity is the default-location fast path. An explicit
 * stock-by-location row for a SKU replaces that SKU's matrix quantity.
 */

export type OpeningStockProductType = "simple" | "variation" | "bundle";

export interface OpeningStockLocationRow {
  location_id: string;
  qty: string | number;
  unit_cost?: string | number | null;
  reference?: string;
  variant_sku?: string;
}

export interface OpeningStockVariant {
  sku: string;
  opening_qty?: number | null;
  cost_price?: number | null;
}

export interface OpeningStockInput {
  productType: OpeningStockProductType;
  defaultLocationId: string | null;
  /** Simple products: "none" ignores simpleQty. Explicit location rows still count. */
  stockChoice?: "none" | "record";
  simpleQty?: string | number | null;
  productCost?: number | null;
  variants?: OpeningStockVariant[];
  locationRows?: OpeningStockLocationRow[];
}

/** Postgres integer used by create_product when it casts opening quantities. */
export const MAX_OPENING_QTY = 2147483647;

function positiveQty(value: string | number | null | undefined): number | null {
  if (openingQuantityError(value)) return null;
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return Number(value);
}

/** Empty is not an error. Zero is not stock. Decimals, negatives, and out-of-range values are. */
export function openingQuantityError(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return "Opening quantity must be a whole number.";
  if (n <= 0) return "Opening quantity must be greater than zero.";
  if (n > MAX_OPENING_QTY) return "Opening quantity is too large.";
  return null;
}

export function visibleOpeningQuantityError(value: string | number | null | undefined): string | null {
  if (value == null || value === "" || value === 0 || value === "0") return null;
  return openingQuantityError(value);
}

function unitCost(value: string | number | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function skuKey(sku: string): string {
  return sku.trim().toLowerCase();
}

export function openingStockTotal(entries: InitialStockEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.qty, 0);
}

export function formatOpeningStockSummary(rows: { locationId: string; locationName: string; qty: number }[]): string {
  const total = rows.reduce((sum, row) => sum + row.qty, 0);
  if (total === 0 || rows.length === 0) return "No opening stock added";
  const locationIds = [...new Set(rows.map((row) => row.locationId).filter(Boolean))];
  if (locationIds.length <= 1) {
    const name = rows.find((row) => row.locationId === locationIds[0])?.locationName ?? rows[0]?.locationName ?? "a location";
    return `Opening stock requested: ${total} units in ${name}`;
  }
  return `Opening stock requested: ${total} units across ${locationIds.length} locations`;
}

/**
 * Location used for matrix (and simple) quantity.
 * One location is used automatically. Several locations require an explicit
 * choice that matches a real location. Name order is never a default.
 */
export function resolveMatrixLocationId(
  locations: { id: string }[],
  chosenId: string | null | undefined,
): string | null {
  if (locations.length === 1) return locations[0].id;
  const chosen = (chosenId ?? "").trim();
  if (!chosen) return null;
  return locations.some((row) => row.id === chosen) ? chosen : null;
}

export function projectOpeningStock(input: OpeningStockInput): InitialStockEntry[] {
  if (input.productType === "bundle") return [];

  const variantSkuByKey = new Map<string, string>();
  for (const variant of input.variants ?? []) {
    const sku = variant.sku.trim();
    if (sku) variantSkuByKey.set(skuKey(sku), sku);
  }

  const explicit: InitialStockEntry[] = [];
  const explicitSkus = new Set<string>();
  for (const row of input.locationRows ?? []) {
    const qty = positiveQty(row.qty);
    const locationId = row.location_id.trim();
    if (!locationId || qty == null) continue;
    if (input.productType === "variation") {
      const sku = variantSkuByKey.get(skuKey(row.variant_sku ?? ""));
      if (!sku) continue;
      explicitSkus.add(skuKey(sku));
      explicit.push({
        sku,
        location_id: locationId,
        qty,
        unit_cost: unitCost(row.unit_cost),
      });
      continue;
    }
    explicit.push({
      location_id: locationId,
      qty,
      unit_cost: unitCost(row.unit_cost),
      reference: row.reference?.trim() || undefined,
    });
  }

  if (input.productType === "simple") {
    if (explicit.length > 0) return explicit;
    if (input.stockChoice === "none") return [];
    const qty = positiveQty(input.simpleQty);
    if (!input.defaultLocationId || qty == null) return [];
    return [{
      location_id: input.defaultLocationId,
      qty,
      unit_cost: input.productCost ?? null,
    }];
  }

  const matrix: InitialStockEntry[] = [];
  if (input.defaultLocationId) {
    for (const variant of input.variants ?? []) {
      const sku = variant.sku.trim();
      const qty = positiveQty(variant.opening_qty);
      if (!sku || qty == null || explicitSkus.has(skuKey(sku))) continue;
      matrix.push({
        sku,
        location_id: input.defaultLocationId,
        qty,
        unit_cost: effectiveCostPrice(variant.cost_price, input.productCost),
      });
    }
  }
  return [...explicit, ...matrix];
}

/**
 * Matrix quantities with no location to land in.
 *
 * `projectOpeningStock` only places variant quantities at a resolved default
 * location. When several locations exist and none is chosen, every entered
 * matrix quantity is dropped while the wizard still reports a clean create —
 * the operator entered stock and none of it was saved. This is a refusal, not a
 * note: the operator either chooses the location or clears the quantities.
 */
export function matrixLocationProblem(input: OpeningStockInput): string | null {
  if (input.productType !== "variation") return null;
  if ((input.defaultLocationId ?? "").trim()) return null;
  const entered = (input.variants ?? []).filter((variant) => (
    variant.sku.trim().length > 0 && positiveQty(variant.opening_qty) != null
  ));
  if (entered.length === 0) return null;
  return `${entered.length} variant quantity${entered.length === 1 ? " has" : "ies have"} no stock location chosen, so ${entered.length === 1 ? "it" : "they"} would not be saved. Choose the location for matrix quantities, or clear the quantities, before continuing.`;
}

/**
 * Rows that carry an operator intent but would be silently dropped by
 * `projectOpeningStock` (no location, invalid quantity, or a variant SKU that
 * is not one of the selected variants), plus the simple-product equivalents.
 *
 * The create RPC is transactional and receives the projected rows only, so a
 * skipped row cannot fail on the server — it simply never existed. Blocking
 * before submit is the only way to avoid a clean success that omitted stock.
 *
 * PHASE1-CREATED-PRODUCT-READINESS-001 extended this to simple products, where
 * an unplaced quantity or a precedence conflict (explicit location rows win and
 * the single quantity field is then ignored) dropped entered stock with no
 * warning at all.
 */
export function openingStockRowProblems(input: OpeningStockInput): string[] {
  if (input.productType === "bundle") return [];
  if (input.productType === "simple") return simpleStockProblems(input);
  const known = new Set(
    (input.variants ?? []).map((variant) => skuKey(variant.sku)).filter((key) => key.length > 0),
  );
  const problems: string[] = [];
  for (const row of input.locationRows ?? []) {
    const location = row.location_id.trim();
    const qtyRaw = String(row.qty ?? "").trim();
    if (!location && !qtyRaw) continue;
    const label = row.variant_sku?.trim() || "this row";
    if (!location) {
      problems.push(`Opening stock for ${label} is missing a location and would not be saved.`);
      continue;
    }
    if (qtyRaw === "") continue;
    if (openingQuantityError(row.qty)) {
      problems.push(`Opening quantity for ${label} is invalid and would not be saved.`);
      continue;
    }
    if (!known.has(skuKey(row.variant_sku ?? ""))) {
      problems.push(`Opening stock for ${label} does not match a selected variant and would be skipped.`);
    }
  }
  return problems;
}

/**
 * Simple products: `projectOpeningStock` uses explicit location rows when any
 * exist and otherwise the single quantity field placed at the resolved
 * location. Both paths can drop what the operator typed, so each case is
 * reported instead of becoming a silent zero.
 */
function simpleStockProblems(input: OpeningStockInput): string[] {
  const problems: string[] = [];
  const usable: number[] = [];
  for (const row of input.locationRows ?? []) {
    const location = row.location_id.trim();
    const qtyRaw = String(row.qty ?? "").trim();
    if (!location && !qtyRaw) continue;
    if (!location) {
      problems.push("An opening stock row is missing a location and would not be saved.");
      continue;
    }
    if (qtyRaw === "") continue;
    if (openingQuantityError(row.qty)) {
      problems.push("An opening stock quantity is invalid and would not be saved.");
      continue;
    }
    usable.push(Number(row.qty));
  }

  const typed = positiveQty(input.simpleQty);
  if (usable.length > 0) {
    if (input.stockChoice !== "none" && typed != null) {
      problems.push(
        `A quantity of ${typed} is also entered above, but stock-by-location rows take precedence for a simple product, so that quantity would not be saved. Clear it, or remove the location rows.`,
      );
    }
    return problems;
  }
  if (input.stockChoice === "none") return problems;
  if (typed != null && !(input.defaultLocationId ?? "").trim()) {
    problems.push(
      `The opening quantity of ${typed} has no stock location, so it would not be saved. Choose a location, or enter the quantity against a location instead.`,
    );
  }
  return problems;
}
